import InfoOutlined from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  CircularProgress,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  type StackProps,
  TextField,
  Tooltip,
  type TextFieldProps,
} from "@mui/material";
import { useField } from "formik";
import { type FocusEvent, useEffect, useState, type ChangeEvent, type FC } from "react";

import "./FormTextField.scss";
import clsx from "clsx";

export interface FormTextFieldProps extends TextFieldProps<"standard"> {
  name: string;
  label?: string;
  withInfo?: string;
  infoTimeout?: number;
  loading?: boolean;
  containerProps?: StackProps;
  helperError?: boolean;
  limit?: number;
  min?: number;
  max?: number;
  counter?: boolean;
  showErrorText?: boolean;
}

export const FormTextField: FC<FormTextFieldProps> = (props) => {
  const { name, label = "", withInfo, showErrorText = true, infoTimeout, containerProps, loading = false, counter = false, min, max, limit,
    onFocusCapture,
    onBlurCapture, placeholder, ...restInputProps } = props;
  const ariaLabel =
    label ||
    (typeof placeholder === "string" ? placeholder : undefined) ||
    name;
  const { className: containerClassName, ...restContainerProps } = containerProps || {};
  const [field, meta, helpers] = useField(name);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const DEFAULT_TIMEOUT = 4000;

  useEffect(() => {
    if (tooltipOpen) {
      const timeout = setTimeout(() => {
        setTooltipOpen(false);
      }, withInfo ? withInfo.split(" ").length * 280 >= DEFAULT_TIMEOUT ? withInfo.split(" ").length * 280 : DEFAULT_TIMEOUT : infoTimeout || DEFAULT_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [tooltipOpen]);

  function handleChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    if (restInputProps.onChange) restInputProps.onChange(event);
    helpers.setValue(event.target.value);
  }

  function onInfoClick() {
    setTooltipOpen((prev) => !prev);
  }

  function handleFocusCapture(event: FocusEvent<HTMLDivElement>) {
    if (!field.value) setTooltipOpen(true);
    if (onFocusCapture) onFocusCapture(event);
  }

  function handleBlurCapture(event: FocusEvent<HTMLDivElement>) {
    setTooltipOpen(false);
    if (onBlurCapture) onBlurCapture(event);
  }

  function handleBlur(
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) {
    if (restInputProps.inputProps?.onBlur) restInputProps.inputProps?.onBlur(event);
    helpers.setTouched(true);
  }

  return (
    <Stack className={clsx("field-container", containerClassName)} {...restContainerProps}>
      {label && (
        <FormLabel sx={{ cursor: "pointer" }} className="field-label" htmlFor={name}>
          {label}
          {withInfo && (
            <Tooltip
              placement="left"
              open={tooltipOpen}
              PopperProps={{
                sx: {
                  maxWidth: "50rem",
                },
              }}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "small",
                    maxWidth: "30vw",
                    width: "max-content",
                  },
                },
              }}
              title={withInfo}
            >
              <IconButton onClick={onInfoClick}>
                <InfoOutlined sx={{ fontSize: "1.2rem" }} color="info" />
              </IconButton>
            </Tooltip>
          )}
        </FormLabel>
      )}
      <TextField
        {...field}
        {...restInputProps}
        placeholder={placeholder}
        FormHelperTextProps={{ className: "helper-text" }}
        id={name}
        label={label || undefined}
        aria-label={!label ? ariaLabel : undefined}
        type={counter ? "number" : restInputProps.type || "text"}
        InputProps={{
          ...restInputProps.InputProps,
          className: `field ${restInputProps.InputProps?.className || ""} ${restInputProps.multiline ? "multiline" : ""}`,
          startAdornment: (
            <InputAdornment position="start">
              {restInputProps.InputProps?.startAdornment}
              {counter && <IconButton onClick={() => helpers.setValue(max && max >= (Number(field.value || 0) + 1) ? Number(field.value || 0) + 1 : max || field.value)}>
                <AddIcon />
              </IconButton>}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {restInputProps.InputProps?.endAdornment}
              {loading && <CircularProgress size={18} />}
              {counter && <IconButton onClick={() => helpers.setValue(min && min <= (Number(field.value || 0) - 1) ? Number(field.value || 0) - 1 : min || field.value)}>
                <RemoveIcon />
              </IconButton>}
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...restInputProps.inputProps,
          className: clsx({ counter }, restInputProps.inputProps?.className),
          maxLength: limit,
          min,
          max
        }}
        error={meta.touched && !!meta.error}
        helperText={showErrorText && meta.touched && meta.error}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      />
    </Stack >
  );
};
